/**
 * Auth Provider - React context and state management
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { toast } from 'sonner';
import type { AuthContextType, AuthResult } from './types';
import {
  signUpOperation,
  signInOperation,
  signInWithGoogleOperation,
  signOutOperation,
  resetPasswordOperation,
  updatePasswordOperation,
  checkEmailExistsOperation,
  checkPhoneExistsOperation,
  getSessionOperation,
  onAuthStateChange,
  ensureProfileExists,
  ensureBusinessProfileExists,
  parseAuthError,
  handleRememberMe,
  shouldClearSessionOnClose,
  clearSessionOnCloseFlag
} from './operations';
import { supabase } from '@/integrations/supabase/client';
import { clearHealthCache } from '@/lib/encryption-health-check';
import { clearDecryptionCache } from '@/lib/decryption-cache';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    let initialSessionChecked = false;

    // Set up auth state listener FIRST (Supabase recommended pattern)
    const { data: { subscription } } = onAuthStateChange(
      (_event, currentSession) => {
        if (mounted) {
          setSession(currentSession);
          setUser(currentSession?.user ?? null);
          if (initialSessionChecked) {
            setLoading(false);
          }
        }
      }
    );

    const initializeAuth = async () => {
      try {
        // Check if session should be cleared (rememberMe was false on last login)
        if (shouldClearSessionOnClose()) {
          await supabase.auth.signOut();
          clearSessionOnCloseFlag();
          if (mounted) {
            setSession(null);
            setUser(null);
          }
          initialSessionChecked = true;
          if (mounted) {
            setLoading(false);
          }
          return;
        }

        const { data: { session: currentSession } } = await getSessionOperation();
        
        if (mounted) {
          setSession(currentSession);
          setUser(currentSession?.user ?? null);
          
          // BACKUP: Profiles serão criados automaticamente pelo trigger
          if (currentSession?.user) {
            setTimeout(() => {
              ensureProfileExists(currentSession.user);
              ensureBusinessProfileExists(currentSession.user);
            }, 100);
          }
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
      } finally {
        initialSessionChecked = true;
        if (mounted) {
          setLoading(false);
        }
      }
    };

    initializeAuth();

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const checkEmailExists = async (email: string): Promise<boolean> => {
    return checkEmailExistsOperation(email);
  };

  const checkPhoneExists = async (phone: string): Promise<boolean> => {
    return checkPhoneExistsOperation(phone);
  };

  const signUp = async (
    email: string, 
    password: string, 
    displayName?: string, 
    phone?: string
  ): Promise<AuthResult> => {
    try {
      const { data, error } = await signUpOperation(email, password, displayName, phone);

      if (error) {
        const isEmptyError = !error.message && Object.keys(error).length === 0;
        
        console.error('[SignUp] Erro ao criar conta:', {
          isEmpty: isEmptyError,
          error: JSON.stringify(error),
          message: error.message,
          status: error.status
        });
        
        if (isEmptyError) {
          toast.error('Servidor sobrecarregado. Tentando novamente...');
          return { error: new Error('504: Servidor sobrecarregado') };
        }
        
        const parsedError = parseAuthError(error);
        
        const isTimeout = error.message?.includes('504') || 
                         error.message?.includes('timeout') ||
                         error.message?.includes('deadline exceeded') ||
                         error.status === 504;
        
        if (isTimeout) {
          return { error };
        }
        
        if (error.message?.includes('User already registered')) {
          toast.error('Este email já está cadastrado. Faça login.');
        } else if (error.message?.includes('rate limit') || parsedError.isRateLimit) {
          toast.error(parsedError.message || 'Muitas tentativas. Aguarde alguns minutos.');
        } else {
          const errorMessage = error.message || parsedError.message || 'Erro ao criar conta. Tente novamente.';
          toast.error(errorMessage);
        }
        
        return { error };
      }

      if (!data.user) {
        console.error('[SignUp] Nenhum usuário criado');
        toast.error('Falha ao criar usuário. Tente novamente.');
        return { error: new Error('Falha ao criar usuário') };
      }

      console.log('[SignUp] Conta criada com sucesso:', {
        userId: data.user.id,
        hasSession: !!data.session
      });

      toast.success('Conta criada com sucesso!');
      return { error: null };
      
    } catch (error: unknown) {
      console.error('[SignUp] Exceção não tratada:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erro ao criar conta. Tente novamente.';
      toast.error(errorMessage);
      const returnError = error instanceof Error ? error : new Error(errorMessage);
      return { error: returnError };
    }
  };

  const signIn = async (
    email: string, 
    password: string, 
    rememberMe: boolean = true
  ): Promise<AuthResult> => {
    const { error } = await signInOperation(email, password);

    if (error) {
      const parsedError = parseAuthError(error);
      toast.error(parsedError.message);
    } else {
      handleRememberMe(rememberMe);
    }

    return { error };
  };

  const signInWithGoogle = async (): Promise<AuthResult> => {
    try {
      const { error } = await signInWithGoogleOperation();

      if (error) {
        console.error('[Auth] Google sign-in error:', error);
        toast.error('Erro ao entrar com Google. Tente novamente.');
        return { error };
      }

      return { error: null };
    } catch (error: unknown) {
      console.error('[Auth] Google sign-in exception:', error);
      toast.error('Erro ao entrar com Google. Tente novamente.');
      const returnError = error instanceof Error ? error : new Error('Erro ao entrar com Google');
      return { error: returnError };
    }
  };

  const signOut = async (): Promise<void> => {
    try {
      // Clear security caches BEFORE logout
      clearHealthCache();
      clearDecryptionCache();
      
      await signOutOperation();
      toast.success('Logout realizado com sucesso');
    } catch (error) {
      console.error('[Auth] Logout error');
      toast.error('Erro ao fazer logout. Tente novamente.');
    }
  };

  const resetPassword = async (email: string): Promise<AuthResult> => {
    const { error } = await resetPasswordOperation(email);

    if (error) {
      const parsedError = parseAuthError(error);
      toast.error(parsedError.message);
    } else {
      toast.success('Verifique seu email para redefinir a senha!');
    }

    return { error };
  };

  const updatePassword = async (newPassword: string): Promise<AuthResult> => {
    try {
      const { error } = await updatePasswordOperation(newPassword);
      return { error };
    } catch (error) {
      console.error('Error in updatePassword:', error);
      return { error: error as any };
    }
  };

  const value: AuthContextType = {
    user,
    session,
    loading,
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
    resetPassword,
    updatePassword,
    checkEmailExists,
    checkPhoneExists,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export { AuthContext };
